import React from 'react';
import { ValidationErrors } from 'constants/validations';
import { DepartmentSelect } from 'components/common/departmentSelect';
import { TransferComponent } from 'components/common/transferComponent';
import {
  AdminSelect,
  Button,
  Col,
  CountrySelect,
  Divider,
  Form,
  Input,
  LanguageSelect,
  Row,
  Select,
  TimePicker,
  Typography,
} from 'components';
import styles from './../styles.module.scss';
import { useAddEdit } from './useAddEdit';
import { emailRegexp } from 'utils/helpers';
import { VALIDATION_MESSAGE } from 'constants/validations/messages';
import { DAYS_OF_WEEK } from 'staticData';
import { USER_TYPES, UserTypesMapping } from '../../../../constants';

const { Item } = Form;
export const UserAddEdit = () => {
  const {
    form,
    userDetails,
    dataToEdit,
    isLoading,
    disableSaveBtn,
    onFinish,
    onCancel,
    targetKeys,
    timeZoneData,
    onChange,
    onSearch,
    handlePageChange,
    isRoleLoading,
    roleData,
    onValuesChange,
    onAccessUserChange,
  } = useAddEdit();
  return (
    <Row justify={'end'} gutter={[0, 32]}>
      <Col span={24}>
        <Row gutter={[0, 24]} justify={'center'}>
          <Col span={24}>
            <Typography.Title className={styles.add_edit_title} level={2}>
              {userDetails ? 'Update' : 'Add'} User
            </Typography.Title>
          </Col>
          <Col span={24} className={styles.form_container}>
            <Form
              onValuesChange={onValuesChange}
              onFinish={onFinish}
              requiredMark={false}
              layout={'vertical'}
              form={form}
            >
              <Row gutter={[16, 16]}>
                <Col span={12}>
                  <Item
                    label={'Email*'}
                    name={'email'}
                    rules={[
                      { required: true, message: ValidationErrors.required('Email') },
                      {
                        validator: (_, value) => {
                          if (value === undefined || value === null || value === '') {
                            return Promise.resolve();
                          }
                          if (!emailRegexp.test(value)) {
                            return Promise.reject(VALIDATION_MESSAGE.EMAIL);
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <Input size={'large'} />
                  </Item>
                </Col>
                <Col span={12}>
                  <Item
                    label={'Name*'}
                    name={'firstName'}
                    rules={[{ required: true, message: ValidationErrors.required('Name') }]}
                  >
                    <Input size={'large'} />
                  </Item>
                </Col>
                <Col span={12}>
                  <Item
                    label={'Surname*'}
                    name={'lastName'}
                    rules={[{ required: true, message: ValidationErrors.required('Surname') }]}
                  >
                    <Input size={'large'} />
                  </Item>
                </Col>
                <Col span={12}>
                  <Item
                    name="languages"
                    label={'Languages*'}
                    rules={[{ required: true, message: ValidationErrors.required('Language') }]}
                  >
                    <LanguageSelect mode={'multiple'} size={'large'} maxTagCount={'responsive'} />
                  </Item>
                </Col>
                <Col span={12}>
                  <Item
                    name="countryId"
                    label={'Country*'}
                    rules={[{ required: true, message: ValidationErrors.required('Country') }]}
                  >
                    <CountrySelect size={'large'} />
                  </Item>
                </Col>
                <Col span={12}>
                  <Item
                    label={'City*'}
                    name={'city'}
                    rules={[{ required: true, message: ValidationErrors.required('City') }]}
                  >
                    <Input size={'large'} />
                  </Item>
                </Col>
                <Col span={12}>
                  <Item
                    label={'Department*'}
                    name="department"
                    rules={[{ required: true, message: ValidationErrors.required('Department') }]}
                  >
                    <DepartmentSelect size={'large'} />
                  </Item>
                </Col>
                <Col span={12}>
                  <Item
                    label={'Title*'}
                    name={'title'}
                    rules={[{ required: true, message: ValidationErrors.required('Title') }]}
                  >
                    <Input size={'large'} />
                  </Item>
                </Col>
                <Col span={12}>
                  <Item
                    name="workingDays"
                    label={'Working Days*'}
                    rules={[{ required: true, message: ValidationErrors.required('Working Days') }]}
                  >
                    <Select options={DAYS_OF_WEEK} mode={'multiple'} size={'large'} maxTagCount={'responsive'} />
                  </Item>
                </Col>
                <Col span={12}>
                  <Item name="workingHours" label={'Working Hours'}>
                    <TimePicker.RangePicker size={'large'} style={{ width: '100%' }} format={'HH:mm'} showNow={true} />
                  </Item>
                </Col>
                <Col span={12}>
                  <Item name="timeZone" label={'Time Zone'}>
                    <Select options={timeZoneData} size={'large'} />
                  </Item>
                </Col>
                <Col span={12}>
                  <Item name="accessSameAsId" label={'Access Same As'}>
                    <AdminSelect size={'large'} onChange={onAccessUserChange as any} />
                  </Item>
                </Col>

                {/* Warehouse Access Section */}
                <Col span={24}>
                  <Divider style={{ margin: '16px 0', color: '#05796B', fontWeight: 'bold' }}>Access Control</Divider>
                </Col>

                <Col span={12}>
                  <Item name="applicationAccess" label={'Application Access*'} rules={[{ required: true }]}>
                    <Select
                      options={Object.values(USER_TYPES).map((i) => ({ label: UserTypesMapping[i], value: i }))}
                      mode={'multiple'}
                      size={'large'}
                      maxTagCount={'responsive'}
                    />
                  </Item>
                </Col>

                <Col span={24}>
                  <Item label="Roles*">
                    <Typography.Text type={'secondary'} style={{ marginBottom: 12, display: 'inline-block' }}>
                      To assign roles, select from the left list and move them to the right panel.
                    </Typography.Text>
                    <Item name="roleIds">
                      <TransferComponent
                        data={roleData}
                        initialData={dataToEdit}
                        targetKeys={targetKeys}
                        onChange={onChange}
                        onSearch={onSearch}
                        isLoading={isRoleLoading}
                        handlePageChange={handlePageChange}
                        titles={{ left: 'All Roles', right: 'Assigned Roles' }}
                        isRole={true}
                      />
                    </Item>
                  </Item>
                </Col>
              </Row>
            </Form>
          </Col>
        </Row>
      </Col>
      <Divider className={styles.divider} />
      <Col flex={'328px'}>
        <Row wrap={false} className="gap-8">
          <Button block size="large" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            block
            size="large"
            disabled={disableSaveBtn}
            type="primary"
            loading={isLoading}
            onClick={() => {
              form?.submit();
            }}
          >
            {userDetails ? 'Save' : 'Add'}
          </Button>
        </Row>
      </Col>
    </Row>
  );
};
